import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { CheckCircle2, RefreshCw, Search } from "lucide-react";
import { format, formatDistanceToNow, parseISO } from "date-fns";
import { ru } from "date-fns/locale";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { Api, type ErrorFingerprintRow, type ErrorEventDetail } from "../api/client";

const SOURCE_LABELS: Record<string, { label: string; cls: string }> = {
  frontend: { label: "Frontend", cls: "bg-astro-violet/20 text-astro-violet" },
  backend: { label: "Backend", cls: "bg-blue-500/20 text-blue-300" },
  job: { label: "Job", cls: "bg-amber-500/20 text-amber-300" },
};

type Segment = "all" | "new" | "regression" | "disappeared";

export function Errors() {
  const qc = useQueryClient();
  const [filterSource, setFilterSource] = useState<string>("");
  const [showResolved, setShowResolved] = useState(false);
  const [fromHours, setFromHours] = useState<number>(168); // 7 дней
  const [search, setSearch] = useState("");
  const [selectedFp, setSelectedFp] = useState<string | null>(null);
  const [segment, setSegment] = useState<Segment>("all");

  const stats = useQuery({
    queryKey: ["error-stats"],
    queryFn: Api.errorStats,
    refetchInterval: 30_000,
  });

  const list = useQuery({
    queryKey: ["error-list", filterSource, showResolved, fromHours, search],
    queryFn: () => Api.errorsList({
      source: filterSource || undefined,
      resolved: showResolved ? undefined : false,
      fromHours,
      search: search.trim() || undefined,
      limit: 100,
    }),
    refetchInterval: 30_000,
  });

  // Сегменты — клиент-side фильтр по уже полученным fingerprint'ам.
  // Для closed-беты client-side хватает; при росте можно вынести в API.
  const segmented = (list.data?.items ?? []).filter((r) => {
    if (segment === "all") return true;
    const now = Date.now();
    if (segment === "new") {
      return new Date(r.firstSeenAt).getTime() > now - 24 * 3600_000;
    }
    if (segment === "regression") {
      return r.resolvedCount > 0 && r.resolvedAt === null;
    }
    if (segment === "disappeared") {
      // Активен (не resolved), но last_seen старше 48ч — фактически перестал воспроизводиться.
      return r.resolvedAt === null && new Date(r.lastSeenAt).getTime() < now - 48 * 3600_000;
    }
    return true;
  });

  const resolve = useMutation({
    mutationFn: (fp: string) => Api.errorResolve(fp),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["error-list"] });
      qc.invalidateQueries({ queryKey: ["error-stats"] });
    },
  });

  return (
    <div>
      <h1 className="font-display text-3xl mb-1">Ошибки</h1>
      <p className="text-white/40 text-sm mb-6">
        Self-hosted JS / backend трекинг. Группировка по fingerprint.
      </p>

      {/* Сводка */}
      <div className="grid grid-cols-4 gap-3 mb-4">
        <StatCard label="За 24ч" value={stats.data?.total24h} variant="error" />
        <StatCard label="Активных групп" value={stats.data?.activeFingerprints} />
        <StatCard label="Новых за 24ч" value={stats.data?.newFingerprints24h} variant="new" />
        <StatCard label="Регрессий 24ч" value={stats.data?.regressions24h} variant="regression" />
      </div>

      {/* Гистограмма по дням (14 дней) */}
      {stats.data?.dailyBuckets && stats.data.dailyBuckets.length > 0 && (
        <div className="rounded-lg border border-white/10 bg-black/20 p-3 mb-6">
          <div className="text-xs text-white/40 mb-2 px-1">События по дням (за 14 дней)</div>
          <ResponsiveContainer width="100%" height={160}>
            <BarChart data={stats.data.dailyBuckets} margin={{ top: 5, right: 10, bottom: 0, left: -10 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
              <XAxis
                dataKey="day"
                tick={{ fill: "rgba(255,255,255,0.4)", fontSize: 10 }}
                tickFormatter={(d: string) => format(parseISO(d), "dd.MM")}
              />
              <YAxis tick={{ fill: "rgba(255,255,255,0.4)", fontSize: 10 }} allowDecimals={false} />
              <Tooltip
                contentStyle={{ background: "#0a0a0f", border: "1px solid rgba(255,255,255,0.1)", fontSize: 12 }}
                labelStyle={{ color: "rgba(255,255,255,0.7)" }}
                labelFormatter={(d: string) => format(parseISO(d), "d MMM", { locale: ru })}
              />
              <Bar dataKey="count" fill="#c97b7b" radius={[2, 2, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Сегменты — chip-фильтры */}
      <div className="flex items-center gap-2 mb-3">
        {(["all", "new", "regression", "disappeared"] as Segment[]).map((s) => (
          <button
            key={s}
            onClick={() => setSegment(s)}
            className={`px-3 py-1 text-xs rounded-full border transition ${
              segment === s
                ? "border-astro-gold/60 bg-astro-gold/10 text-astro-gold"
                : "border-white/10 bg-black/30 text-white/60 hover:text-white"
            }`}
          >
            {s === "all" && "Все"}
            {s === "new" && "Новые (24ч)"}
            {s === "regression" && "Регрессии"}
            {s === "disappeared" && "Исчезли (>48ч)"}
          </button>
        ))}
      </div>

      {/* Фильтры */}
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <select
          value={filterSource}
          onChange={(e) => setFilterSource(e.target.value)}
          className="bg-black/40 border border-white/10 rounded px-3 py-1.5 text-sm"
        >
          <option value="">Все источники</option>
          <option value="frontend">Frontend</option>
          <option value="backend">Backend</option>
          <option value="job">Job</option>
        </select>

        <select
          value={fromHours}
          onChange={(e) => setFromHours(Number(e.target.value))}
          className="bg-black/40 border border-white/10 rounded px-3 py-1.5 text-sm"
        >
          <option value={24}>Последние 24 часа</option>
          <option value={168}>Последние 7 дней</option>
          <option value={720}>Последние 30 дней</option>
          <option value={8760}>За всё время</option>
        </select>

        <label className="flex items-center gap-2 text-sm text-white/70 cursor-pointer">
          <input
            type="checkbox"
            checked={showResolved}
            onChange={(e) => setShowResolved(e.target.checked)}
            className="accent-astro-gold"
          />
          включая resolved
        </label>

        <div className="flex items-center gap-2 ml-auto">
          <Search size={14} className="text-white/40" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Текст в сообщении / типе"
            className="bg-black/40 border border-white/10 rounded px-3 py-1.5 text-sm w-64"
          />
          <button
            onClick={() => list.refetch()}
            className="p-1.5 text-white/50 hover:text-white"
            title="Обновить"
          >
            <RefreshCw size={14} />
          </button>
        </div>
      </div>

      {/* Список + детальный пейн */}
      <div className="grid grid-cols-[1fr_1fr] gap-4">
        <ErrorList
          rows={segmented}
          loading={list.isLoading}
          selectedFp={selectedFp}
          onSelect={setSelectedFp}
          onResolve={(fp) => resolve.mutate(fp)}
          resolvingFp={resolve.isPending ? resolve.variables ?? null : null}
        />

        {selectedFp ? (
          <ErrorDetail fingerprint={selectedFp} />
        ) : (
          <div className="rounded-lg border border-white/10 bg-black/20 p-8 text-center text-white/40 text-sm h-full flex items-center justify-center">
            Выбери группу слева чтобы увидеть события
          </div>
        )}
      </div>
    </div>
  );
}

// -------------------- Sub-components --------------------

function StatCard({
  label, value, variant,
}: { label: string; value: number | undefined; variant?: "error" | "new" | "regression" }) {
  const bg = variant === "error" ? "bg-red-500/10 border-red-500/30" :
             variant === "new" ? "bg-amber-500/10 border-amber-500/30" :
             variant === "regression" ? "bg-purple-500/10 border-purple-500/30" :
             "bg-white/5 border-white/10";
  return (
    <div className={`rounded-lg border p-4 ${bg}`}>
      <div className="text-white/50 text-xs mb-1">{label}</div>
      <div className="text-2xl font-medium">{value ?? "—"}</div>
    </div>
  );
}

function ErrorList({
  rows, loading, selectedFp, onSelect, onResolve, resolvingFp,
}: {
  rows: ErrorFingerprintRow[];
  loading: boolean;
  selectedFp: string | null;
  onSelect: (fp: string) => void;
  onResolve: (fp: string) => void;
  resolvingFp: string | null;
}) {
  if (loading) {
    return <div className="text-white/40 text-sm p-4">Загружаем…</div>;
  }
  if (rows.length === 0) {
    return (
      <div className="rounded-lg border border-white/10 bg-black/20 p-8 text-center text-white/40 text-sm">
        <CheckCircle2 size={32} className="mx-auto mb-2 text-green-500/50" />
        Ни одной ошибки — это хорошо.
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {rows.map((r) => {
        const isResolved = r.resolvedAt !== null;
        const isSelected = r.fingerprint === selectedFp;
        const src = SOURCE_LABELS[r.source] ?? { label: r.source, cls: "bg-white/10 text-white/70" };
        const isRegression = r.resolvedCount > 0 && !isResolved;

        return (
          <div
            key={r.fingerprint}
            onClick={() => onSelect(r.fingerprint)}
            className={`rounded-lg border p-3 cursor-pointer transition ${
              isSelected
                ? "border-astro-gold/60 bg-astro-gold/5"
                : "border-white/10 bg-black/20 hover:bg-black/30"
            } ${isResolved ? "opacity-50" : ""}`}
          >
            <div className="flex items-start gap-2">
              <span className={`text-[10px] uppercase px-1.5 py-0.5 rounded ${src.cls}`}>
                {src.label}
              </span>
              {isResolved && (
                <span className="text-[10px] uppercase px-1.5 py-0.5 rounded bg-green-500/20 text-green-300">
                  resolved
                </span>
              )}
              {isRegression && (
                <span className="text-[10px] uppercase px-1.5 py-0.5 rounded bg-purple-500/20 text-purple-300">
                  regression
                </span>
              )}
              <span className="text-white/40 text-xs ml-auto">
                {formatDistanceToNow(parseISO(r.lastSeenAt), { addSuffix: true, locale: ru })}
              </span>
            </div>

            <div className="mt-1.5 text-sm text-white/90 font-medium truncate" title={r.errorType ?? ""}>
              {r.errorType ?? "Unknown error"}
            </div>
            <div className="text-xs text-white/60 line-clamp-2 mt-0.5" title={r.sampleMessage ?? ""}>
              {r.sampleMessage ?? "(no message)"}
            </div>

            <div className="flex items-center gap-3 mt-2 text-xs text-white/40">
              <span title="Всего event'ов за всё время">∑ {r.totalCount}</span>
              <span title="Event'ов за последние 24ч">24h: {r.events24h}</span>
              <span className="text-white/30">·</span>
              <span className="font-mono text-[10px]">{r.fingerprint.slice(0, 8)}</span>

              {!isResolved && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onResolve(r.fingerprint);
                  }}
                  disabled={resolvingFp === r.fingerprint}
                  className="ml-auto px-2 py-0.5 text-[11px] bg-green-500/15 text-green-300 hover:bg-green-500/25 rounded border border-green-500/30 transition disabled:opacity-50"
                >
                  {resolvingFp === r.fingerprint ? "…" : "Resolve"}
                </button>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function ErrorDetail({ fingerprint }: { fingerprint: string }) {
  const events = useQuery({
    queryKey: ["error-events", fingerprint],
    queryFn: () => Api.errorEvents(fingerprint, 20),
  });
  const [selectedEvent, setSelectedEvent] = useState<ErrorEventDetail | null>(null);

  if (events.isLoading) {
    return <div className="text-white/40 text-sm p-4">Загружаем события…</div>;
  }

  return (
    <div className="rounded-lg border border-white/10 bg-black/20 p-4 max-h-[80vh] overflow-y-auto">
      <div className="text-xs text-white/40 mb-3 font-mono">{fingerprint}</div>

      <div className="space-y-2 mb-4">
        {(events.data?.items ?? []).map((e) => (
          <button
            key={e.id}
            onClick={() => setSelectedEvent(e)}
            className={`w-full text-left rounded px-3 py-2 text-xs border transition ${
              selectedEvent?.id === e.id
                ? "border-astro-gold/60 bg-astro-gold/5"
                : "border-white/10 bg-black/20 hover:bg-black/30"
            }`}
          >
            <div className="flex items-center gap-2 text-white/50">
              <span>{format(parseISO(e.createdAt), "dd MMM HH:mm:ss", { locale: ru })}</span>
              {e.url && <span className="truncate">· {e.url}</span>}
              {e.appUserId !== null && (
                <span className="ml-auto text-astro-gold/70">u#{e.appUserId}</span>
              )}
            </div>
            <div className="text-white/80 truncate mt-0.5">{e.message}</div>
          </button>
        ))}
      </div>

      {selectedEvent && (
        <div className="mt-4 pt-4 border-t border-white/10 space-y-3 text-xs">
          <KV label="Время">{format(parseISO(selectedEvent.createdAt), "PPpp", { locale: ru })}</KV>
          <KV label="Level">{selectedEvent.level}</KV>
          <KV label="URL">{selectedEvent.url ?? "—"}</KV>
          {selectedEvent.appUserId !== null && <KV label="App User">#{selectedEvent.appUserId}</KV>}
          {selectedEvent.appVersion && <KV label="Версия">{selectedEvent.appVersion}</KV>}
          {selectedEvent.userAgent && <KV label="UA"><span className="break-all">{selectedEvent.userAgent}</span></KV>}

          <div>
            <div className="text-white/40 text-[10px] uppercase mb-1">Stack trace</div>
            <pre className="bg-black/40 border border-white/10 rounded p-2 text-[11px] font-mono whitespace-pre-wrap break-all max-h-64 overflow-y-auto">
              {selectedEvent.stackTraceSymbolicated ?? selectedEvent.stackTrace ?? "(no stack)"}
            </pre>
          </div>

          {selectedEvent.context && (
            <div>
              <div className="text-white/40 text-[10px] uppercase mb-1">Context</div>
              <pre className="bg-black/40 border border-white/10 rounded p-2 text-[11px] font-mono whitespace-pre-wrap break-all max-h-48 overflow-y-auto">
                {tryPrettifyJson(selectedEvent.context)}
              </pre>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function KV({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex gap-3">
      <span className="text-white/40 w-20 shrink-0">{label}</span>
      <span className="text-white/80 flex-1 break-words">{children}</span>
    </div>
  );
}

function tryPrettifyJson(s: string): string {
  try {
    return JSON.stringify(JSON.parse(s), null, 2);
  } catch {
    return s;
  }
}
